/**
 * Pantalla de Academias
 * 
 * REESTRUCTURADA para seguir exactamente el mismo patrón visual que Programas Académicos:
 * - Mismo layout y estructura de componentes
 * - HeaderGlobal con título
 * - Barra de búsqueda con botón Buscar y + Nuevo
 * - Tabla con paginación
 * 
 * Integración:
 * - Usa el API en /api/academias.js con operaciones CRUD
 * - Reutiliza estilos de ProgramasAcademicos para consistencia visual
 */

import React, { useState, useEffect } from "react";
import { getAcademias, createAcademia, updateAcademia, deleteAcademia } from '../../api/academias';
import styles from "../../styles/PantallaDeProgramasAcademicos.v2.module.css";
import estudiantesStyles from "../../styles/EstudiantesStyles/Estudiantes.module.css";
import Sidebar from "../../components/Sidebar";
import { useSidebar } from "../../components/Sidebar";
import HeaderGlobal from "../../components/HeaderGlobal";
import LoaderOverlay from "../../components/LoaderOverlay";
import AcademiasTable from "./AcademiasTable";
import NuevaAcademiaModal from "./NuevaAcademiaModal";
import Toast from "../DocentesScreen/Toast";

function PantallaDeAcademias() {
  const { isCollapsed } = useSidebar();
  const [openNew, setOpenNew] = useState(false);
  const [editData, setEditData] = useState(null);
  const [mode, setMode] = useState('create'); // 'create' | 'view' | 'edit'
  const [query, setQuery] = useState('');
  const [academias, setAcademias] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cargar academias al montar
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await getAcademias();
        if (mounted) setAcademias(data);
      } catch (e) {
        console.error('Error cargando academias:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  // Handler para búsqueda
  const handleSearch = async () => {
    try {
      setLoading(true);
      const data = await getAcademias(query);
      setAcademias(data);
    } catch (e) {
      console.error('Error buscando academias:', e);
    } finally {
      setLoading(false);
    }
  };

  // Handler para abrir modal de registro (nuevo)
  const handleRegister = () => {
    setEditData(null);
    setMode('create');
    setOpenNew(true);
  };

  // Handler para ver academia
  const handleView = (row) => {
    setEditData(row);
    setMode('view');
    setOpenNew(true);
  };

  // Handler para editar academia
  const handleEdit = (row) => {
    setEditData(row);
    setMode('edit');
    setOpenNew(true);
  };

  // Handler para eliminar academia
  const handleDelete = async (row) => {
    if (!window.confirm('¿Eliminar esta academia?')) return;
    try {
      await deleteAcademia(row.id);
      const data = await getAcademias(query);
      setAcademias(data);
      window.dispatchEvent(new CustomEvent('toast:show', {
        detail: { type: 'success', message: 'Academia eliminada correctamente' }
      }));
    } catch (e) {
      console.error('Error eliminando academia:', e);
      window.dispatchEvent(new CustomEvent('toast:show', {
        detail: {
          type: 'error',
          message: e?.message || 'No se pudo eliminar la academia (puede tener registros relacionados).'
        }
      }));
    }
  };

  // Handler para cerrar modal
  const handleClose = () => {
    setOpenNew(false);
    setEditData(null);
  };

  // Handler para guardar (crear o actualizar)
  const handleSubmit = async (data) => {
    try {
      if (editData && mode === 'edit') {
        await updateAcademia(editData.id, data);
        window.dispatchEvent(new CustomEvent('toast:show', {
          detail: { type: 'success', message: 'Academia actualizada' }
        }));
      } else {
        await createAcademia(data);
        window.dispatchEvent(new CustomEvent('toast:show', {
          detail: { type: 'success', message: 'Academia creada' }
        }));
      }
      const updated = await getAcademias(query);
      setAcademias(updated);
      setOpenNew(false);
      setEditData(null);
    } catch (err) {
      console.error('[PantallaDeAcademias] Error al guardar:', err);
      window.dispatchEvent(new CustomEvent('toast:show', {
        detail: {
          type: 'error',
          message: err?.message || 'No se pudo guardar la academia.'
        }
      }));
    }
  };

  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.layout} ${isCollapsed ? styles.sidebarCollapsedLayout : ''}`}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <HeaderGlobal title="Academias" />
          <main className={styles.mainContent}>
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loading} label="Cargando academias..." />
                
                {/* Sección de búsqueda - igual que Programas Académicos */}
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                    <div className={styles.programsSearchRow}>
                      <div className={estudiantesStyles.searchInputGroup}>
                        <svg
                          className={estudiantesStyles.searchIcon}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                            stroke="#898d8d"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <input
                          type="text"
                          className={estudiantesStyles.searchInput}
                          placeholder="Buscar Academia..."
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSearch();
                          }}
                        />
                      </div>

                      <button
                        className={styles.buscarButtonProgramas}
                        onClick={handleSearch}
                      >
                        Buscar
                      </button>

                      <button
                        className={styles.newProgramButton}
                        onClick={handleRegister}
                      >
                        + Nueva Academia
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tabla de Academias */}
                <AcademiasTable
                  academias={academias}
                  loading={loading}
                  query={query}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              </div>
            </div>
          </main>

          {/* Modal para crear/editar */}
          {openNew && (
            <NuevaAcademiaModal
              open={openNew}
              onClose={handleClose}
              onSubmit={handleSubmit}
              initialData={editData}
              mode={mode}
            />
          )}
        </div>
      </div>
        <Toast />
    </div>
  );
}

export default PantallaDeAcademias;
